import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { api } from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import {
  claimSourceConnectPrompt,
  forceClaimSourceConnectPrompt,
  isAnySourceConnectPromptOpen,
  onSourceConnectPromptClosed,
  releaseSourceConnectPrompt,
} from "./source-connect-prompt-guard";

const DISMISS_KEY = "as-google-calendar-dismiss";
const DISMISS_FOREVER_KEY = "as-google-calendar-dismiss-forever";

export function openGoogleCalendarConsent(source?: "google_meet") {
  sessionStorage.removeItem(DISMISS_KEY);
  localStorage.removeItem(DISMISS_FOREVER_KEY);
  window.dispatchEvent(
    new CustomEvent("as-google-calendar-prompt", {
      detail: source ? { source } : undefined,
    }),
  );
}

/**
 * After Google SSO (or when Meet is not connected), explain exactly what Google's
 * consent screen will ask for before redirecting to Calendar/Meet OAuth.
 */
export function GoogleCalendarConnectPrompt() {
  const { orgId, canManage } = useOrg();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // When opened from the Google Meet card the title must match the provider
  // the user clicked (Meet events arrive via the Calendar API either way).
  const [meetMode, setMeetMode] = useState(false);

  useEffect(() => {
    if (!orgId || !canManage) return;

    async function maybeOpen(force = false) {
      if (
        !force &&
        (sessionStorage.getItem(DISMISS_KEY) === "1" ||
          localStorage.getItem(DISMISS_FOREVER_KEY) === "1")
      ) {
        return;
      }
      if (!force && isAnySourceConnectPromptOpen()) return;
      try {
        const [meet, googleAuth] = await Promise.all([
          api<{ connections: Array<{ status: string }> }>(
            `/api/accounts/${orgId}/sources/google_meet`,
          ).catch(() => ({ connections: [] as Array<{ status: string }> })),
          api<{ enabled: boolean }>("/api/auth/google").catch(() => ({
            enabled: false,
          })),
        ]);
        if (!googleAuth.enabled) return;
        const hasActive = meet.connections.some((c) => c.status === "active");
        if (hasActive) return;
        if (!force && !claimSourceConnectPrompt()) return;
        if (force) forceClaimSourceConnectPrompt();
        setOpen(true);
      } catch {
        /* ignore */
      }
    }

    void maybeOpen(false);
    const onForce = (event: Event) => {
      const source = (event as CustomEvent<{ source?: string }>).detail?.source;
      setMeetMode(source === "google_meet");
      void maybeOpen(true);
    };
    const onClosed = () => void maybeOpen(false);
    window.addEventListener("as-google-calendar-prompt", onForce);
    const removeClosed = onSourceConnectPromptClosed(onClosed);
    return () => {
      window.removeEventListener("as-google-calendar-prompt", onForce);
      removeClosed();
    };
  }, [orgId, canManage]);

  function skip() {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
    releaseSourceConnectPrompt();
  }

  function dismissForever() {
    localStorage.setItem(DISMISS_FOREVER_KEY, "1");
    sessionStorage.removeItem(DISMISS_KEY);
    setOpen(false);
    releaseSourceConnectPrompt();
  }

  function continueToGoogle() {
    setBusy(true);
    const returnPath = encodeURIComponent("/integrations");
    window.location.href = `/oauth/google/calendar/start?accountId=${encodeURIComponent(orgId)}&returnPath=${returnPath}`;
  }

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) skip();
        else setOpen(true);
      }}
      title={meetMode ? "Connect Google Meet" : "Connect Google Calendar"}
      description="Google will ask you to allow AttendeeSync to access your account. You can revoke access anytime in your Google Account settings."
      impact={
        <ul className="list-disc space-y-1 pl-4 text-sm text-rs-ink">
          <li>
            See your email address and basic profile (already used for sign-in)
          </li>
          <li>View your Google Calendar events and their attendees</li>
          <li>View Google Meet space details for events that include Meet</li>
          <li>
            AttendeeSync will use this to discover meetings and create sync
            streams when Auto-Create is enabled
          </li>
        </ul>
      }
      contentClassName="w-[min(37rem,calc(100vw-2rem))]"
      cancelLabel="Skip"
      footerLeft={
        <Button
          type="button"
          variant="link"
          className="text-rs-danger"
          disabled={busy}
          onClick={dismissForever}
        >
          Dismiss Forever
        </Button>
      }
      confirmLabel={busy ? "Redirecting…" : "Continue To Google"}
      busy={busy}
      onConfirm={() => continueToGoogle()}
    />
  );
}
